import { useEffect } from 'react';
import type { Session } from '@supabase/supabase-js';
import { config } from './config';

const POSTHOG_KEY =
  import.meta.env.VITE_POSTHOG_KEY ??
  'phc_AclrQtKm7WpqHP7XqTNQPOs8wBnVQ2Hqp9uQqG1cK90';
const POSTHOG_HOST = 'https://us.i.posthog.com';
const POSTHOG_ENABLED = import.meta.env.VITE_ENABLE_POSTHOG !== 'false';
const DISTINCT_ID_KEY = 'rvlry.admin.analytics.distinct_id';

function getProjectRef() {
  try {
    return new URL(config.supabaseUrl).host.split('.')[0] ?? 'unknown';
  } catch {
    return 'unknown';
  }
}

function getDistinctId() {
  try {
    const stored = window.localStorage.getItem(DISTINCT_ID_KEY);
    if (stored) return stored;
    const next = crypto.randomUUID();
    window.localStorage.setItem(DISTINCT_ID_KEY, next);
    return next;
  } catch {
    return 'anonymous-admin-browser';
  }
}

function sendToPosthog(event: string, properties: Record<string, unknown>) {
  if (!POSTHOG_ENABLED || !POSTHOG_KEY || typeof window === 'undefined') return;
  const body = {
    api_key: POSTHOG_KEY,
    event,
    distinct_id: getDistinctId(),
    properties,
    timestamp: new Date().toISOString(),
  };
  const payload = JSON.stringify(body);
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(`${POSTHOG_HOST}/capture/`, blob)) return;
    }
    void fetch(`${POSTHOG_HOST}/capture/`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Analytics must never block admin work.
  }
}

export function trackAdminEvent(event: string, properties: Record<string, unknown> = {}) {
  const route = `${window.location.pathname}${window.location.search}`;
  sendToPosthog(event, {
    surface: 'admin',
    source: 'web-hub',
    env: config.hubActiveEnv,
    project_ref: getProjectRef(),
    route,
    ...properties,
  });
}

export function identifyAdminUser(session: Session | null) {
  if (!session?.user?.id) return;
  trackAdminEvent('admin_auth_state', {
    action: 'identify',
    user_role: 'admin',
    admin_user_id: session.user.id,
    email_domain: session.user.email?.split('@')[1] ?? null,
  });
}

function describeElement(target: HTMLElement) {
  const explicitLabel = target.getAttribute('data-analytics-label');
  const aria = target.getAttribute('aria-label');
  const title = target.getAttribute('title');
  const name = target.getAttribute('name');
  const id = target.id || null;
  const className = typeof target.className === 'string' ? target.className.split(/\s+/).slice(0, 3).join(' ') : null;
  return {
    element_tag: target.tagName.toLowerCase(),
    element_label: (explicitLabel || aria || title || name || target.getAttribute('type') || target.tagName.toLowerCase()).slice(0, 80),
    element_id: id,
    element_class: className,
  };
}

export function useAdminClickAutocapture(activeTab: string, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (event: MouseEvent) => {
      const target = event.target instanceof HTMLElement
        ? event.target.closest('button,a,input,select,textarea,summary,[role="button"]')
        : null;
      if (!(target instanceof HTMLElement)) return;
      trackAdminEvent('admin_click', {
        action: 'click',
        tab: activeTab,
        ...describeElement(target),
      });
    };
    document.addEventListener('click', handler, { capture: true });
    return () => document.removeEventListener('click', handler, { capture: true });
  }, [activeTab, enabled]);
}
